import type { HomeCustomConfig } from '@/pages/home/config/types';
import { ubtSendError } from '../utils/ubt';
import URLUtils from '../utils/URLUtils';
import { ActivityPageTypeEnums, ActivityUndertakeTypeEnums } from './activity-enums';
import { session } from '../utils/storage';
import { getWisdomConfig, preloadWisdomClient } from '../wisdom';
import { HomeActivityConfigMap, VerifyCreditActivityConfig, VerifyCreditActivityConfigMap } from './types';

/** 活动承接类型在session中存储的key */
const SESSION_KEY_UNDERTAKE_TYPE = 'session_key_undertake_type';

/** url query中的活动承接类型 */
let urlQueryUndertakeType: string | null = null;

/** 活动承接类型 */
let activityUndertakeType: ActivityUndertakeTypeEnums | null = null;

/** 初始化活动承接类型 */
function initActivityUndertakeType() {
  if (urlQueryUndertakeType) {
    switch (urlQueryUndertakeType) {
      case ActivityUndertakeTypeEnums.PHONE_BILL:
        activityUndertakeType = ActivityUndertakeTypeEnums.PHONE_BILL;
        break;
      case ActivityUndertakeTypeEnums.BIKE_CARD_7:
        activityUndertakeType = ActivityUndertakeTypeEnums.BIKE_CARD_7;
        break;
      case ActivityUndertakeTypeEnums.CARPOOL_COUPON_PACKAGE:
        activityUndertakeType = ActivityUndertakeTypeEnums.CARPOOL_COUPON_PACKAGE;
        break;
      case ActivityUndertakeTypeEnums.LOAN_ACTIVITY:
        activityUndertakeType = ActivityUndertakeTypeEnums.LOAN_ACTIVITY;
        break;
      case ActivityUndertakeTypeEnums.INTEREST_FREE_ACTIVITY:
        activityUndertakeType = ActivityUndertakeTypeEnums.INTEREST_FREE_ACTIVITY;
        break;
      default:
        /** 后续创建新的主题活动，无需再定义新的枚举值 */
        activityUndertakeType = urlQueryUndertakeType as ActivityUndertakeTypeEnums;
        break;
    }
  }
}

/** 初始URL参数活动类型(session缓存) */
const initUrlQueryUndertakeType = (): void => {
  try {
    const urlQuery = URLUtils.getQueryMap();

    urlQueryUndertakeType = (
      urlQuery.activityUndertakeType || session.get(SESSION_KEY_UNDERTAKE_TYPE)
    ) as string;

    if (urlQueryUndertakeType) {
      /** 缓存的活动承接类型，用于新老项目跳转丢失问题 */
      session.set(SESSION_KEY_UNDERTAKE_TYPE, urlQueryUndertakeType);
      /** 预初始化wisdom-sdk实例 */
      preloadWisdomClient();
      /** 定义活动类型 */
      initActivityUndertakeType();
    }
  } catch (err) {
    ubtSendError(err as Error);
  }
};

/**
 * 获取活动承接类型
 * @returns 活动承接类型: 送话费
 */
const getActivityUndertakeType = (): ActivityUndertakeTypeEnums | null => activityUndertakeType;

/** 活动承接页面配置缓存(wisdom配置) */
const activityModulesConfigCache: {
  [ActivityPageTypeEnums.HOME]: HomeActivityConfigMap | null;
  [ActivityPageTypeEnums.VERIFY_CREDIT]: VerifyCreditActivityConfigMap | null;
} = {
  [ActivityPageTypeEnums.HOME]: null,
  [ActivityPageTypeEnums.VERIFY_CREDIT]: null,
};

/** 初始化活动承接页面配置 */
const initActivityModulesConfig = async (
  pageType: ActivityPageTypeEnums,
): Promise<void> => {
  const configCache = activityModulesConfigCache[pageType];

  /** 若已初始化配置缓存，则退出流程 */
  if (configCache) return;

  if (pageType === ActivityPageTypeEnums.HOME) {
    const config = await getWisdomConfig<HomeActivityConfigMap>('themeActivityHomeModulesConfig');

    activityModulesConfigCache[ActivityPageTypeEnums.HOME] = config || {};
  } else if (pageType === ActivityPageTypeEnums.VERIFY_CREDIT) {
    const config = await getWisdomConfig<VerifyCreditActivityConfigMap>('activityVerifyCreditConfig');

    activityModulesConfigCache[ActivityPageTypeEnums.VERIFY_CREDIT] = config || {};
  }
};

/**
 * 读取首页-指定活动主题的配置
 * @param activityType 活动主题类型
 * @returns 活动配置
 */
const getHomeActivityModulesConfig = (): HomeCustomConfig => {
  const configCache = activityModulesConfigCache[ActivityPageTypeEnums.HOME];
  const activityType = getActivityUndertakeType();

  if (configCache && activityType && configCache[activityType]) {
    return configCache[activityType] as HomeCustomConfig;
  }

  if (activityType) {
    ubtSendError({
      name: 'getHomeActivityModulesConfig-error',
      message: `activityType-${activityType}`,
    });
  }

  return {} as HomeCustomConfig;
};

/**
 * 读取资料页-指定活动主题的配置
 * @param activityType 活动主题类型
 * @returns 活动配置
 */
const getVerifyCreditActivityModulesConfig = (): VerifyCreditActivityConfig => {
  const configCache = activityModulesConfigCache[ActivityPageTypeEnums.VERIFY_CREDIT];
  const activityType = getActivityUndertakeType();

  if (configCache && activityType && configCache[activityType]) {
    return configCache[activityType] as VerifyCreditActivityConfig;
  }

  if (activityType) {
    ubtSendError({
      name: 'getVerifyCreditActivityModulesConfig-error',
      message: `activityType-${activityType}`,
    });
  }

  return {} as VerifyCreditActivityConfig;
};

/**
 * 获取活动承接身份证后部分标题文案
 * @returns 活动承接身份证后部分标题文案
 */
const getVerifyIdentityTitleLatterPartText = ()
  : string => getVerifyCreditActivityModulesConfig().identityTitleLatterPartText || '';

/** 项目初始化时先获取url query中的活动承接类型 */
initUrlQueryUndertakeType();

export {
  ActivityPageTypeEnums,
  ActivityUndertakeTypeEnums,
  getActivityUndertakeType,
  initActivityModulesConfig,
  getHomeActivityModulesConfig,
  getVerifyCreditActivityModulesConfig,
  getVerifyIdentityTitleLatterPartText,
};
